import router from "@/router";
import methods from "@/global/methods.js";
import apivalidation from "@/global/apivalidation.js";

export default {
    // Determine whether something should happen after an option has been selected by the user
    autoNext: function (vuex, arg) {
        // Set a timer to prevent a new page from loading too quickly
        this.commit(`changeGlobal`, {
            variableName: `pageLoadIsAllowed`,
            newValue: false
        });

        setTimeout(() => {
            this.commit(`changeGlobal`, {
                variableName: `pageLoadIsAllowed`,
                newValue: true
            });
        }, 500);

        //Settimeout is needed because this script is otherwise run before the browser processes the click
        setTimeout(() => {
            let allQuestionsareValid = vuex.state.page.questions.every(q => q.isValid);
            this.commit(`updateFormValidation`);

            // Go to the next page if autoNext is on and the form is invalid
            if (
                arg.questionData.settings?.autoNext &&
                vuex.state.global.formIsValid &&
                allQuestionsareValid
            ) {
                vuex.dispatch(`loadPageData`, { actionType: `Default` });
            }
        }, 10);
    },
    pageStartAtTop: function () {
        // Needed to have Firefox scroll back to the top when a new page is loaded
        document.getElementById(`app`)?.scrollIntoView();
    },
    // Load the data of the pages asynchronously and add them to the store
    loadPageData(vuex, arg) {
        let page = vuex.state.page;
        let surveyInfo = vuex.state.surveyInfo;
        let pageDataInterval;

        // Check whether all the questions have been validated as valid
        // The empty question is a special case as nothing has to be validated, so the isValid property doesn't exist
        let allQuestionsareValid = vuex.state.page?.questions.every(q => q.isValid || q.type === `Empty`);

        // This is needed to reset the countdowngo option when navigating between questions
        if (page) page.countdownGoSettings.countdownSeconds = -1;

        // If something else is already loading, we don't want more calls
        if (vuex.state.global.showLoader) return;

        // When we go back, we don't want to activate any validation as nothing is being submitted
        if (arg.actionType !== `Previous`) this.commit(`updateFormValidation`);

        // If the user wants to go to the next page button the form is not valid, stop it
        if (
            (!vuex.state.global.formIsValid ||
                !allQuestionsareValid) &&
            arg.actionType !== `Previous` &&
            vuex.state.global.nextButtonClicked
        ) {
            // When the user is prevented from navigating, focus and blur on one of the invalid fields so that the error message will be shown
            let invalidFields = document.querySelectorAll(
                `textarea:invalid, input:invalid`
            );

            invalidFields.forEach(field => {
                field.focus();
                field.blur();
            });
            return;
        }

        let data = {
            token: surveyInfo.token,
            actionType: arg.actionType,
        };

        // We need to send a stripped down page object + the most recent id's to the server when navigating to a new page from another page
        if (arg.actionType === `Default` && page !== undefined) {
            // Send the newly updated page object to the server
            data.surveyPage = methods.toStrippedQuestionObj(page, vuex.state.type);
            data.surveyId = page.surveyId;
            data.respondentId = page.respondentId;
            data.sessionId = page.sessionId;
            data.pageId = page.pageId;
        }

        // When starting a new survey or when going to a previous page, we can use the ids that are stored in the surveyinfo object
        if (arg.actionType === `Start` || arg.actionType === `Previous`) {
            data.surveyId = surveyInfo.surveyId;
            data.respondentId = surveyInfo.respondentId;
            data.sessionId = surveyInfo.sessionId;
            data.currentPageId = surveyInfo.currentPageId;
            data.pageId = surveyInfo.pageId;
        }

        if (arg.actionType === `Previous`) {
            data.pageId = page.pageId;
        }

        methods.xhr({
            url: `${surveyInfo.domain}/v1/Questionnaire/Next`,
            data: data,
            setSurveyId: true,
            callback: (xhr) => {
                // In the case of autonext we want to wait for a timer to finish, if it hasn't already. 
                // This is to prevent a new page loading instantly before the user receives visual feedback
                if (vuex.state.global.pageLoadIsAllowed === false) {
                    pageDataInterval = setInterval(() => {
                        if (vuex.state.global.pageLoadIsAllowed) {
                            vuex.dispatch(`nextCallback`, { arg, xhr });
                            clearInterval(pageDataInterval);
                        }
                    }, 10)
                } else {
                    vuex.dispatch(`nextCallback`, { arg, xhr });
                }
            },
        });
    },
    nextCallback: function (vuex, parameters) {
        let response = JSON.parse(parameters.xhr.responseText);
        let actionObject = response.actionObject;
        let currentCulture = vuex.state.surveyInfo.culture || vuex.state.surveyInfo.queryParams.culture;
        let newCulture = response.culture;

        // This questiontype can't and shouldn't be tested
        if (response.action !== `Redirect`) {
            apivalidation.validate();
            apivalidation.validateQuestionObj(actionObject);
        }

        if (newCulture !== undefined && newCulture !== null && currentCulture !== newCulture) {
            this.commit(`addSurveyInfo`, { culture: newCulture });
            vuex.dispatch("loadText", newCulture);
        }

        this.commit(`addSurveyInfo`, { actionObject });
        this.commit(`addServerMessages`, response.message);

        if (actionObject && response.action !== `SurveyEnd` && response.action !== `Redirect`) {
            this.commit(`addPageData`, { page: actionObject });
        }

        this.commit(`changeGlobal`, {
            variableName: `nextButtonClicked`,
            newValue: false,
        });
        this.commit(`changeGlobal`, {
            variableName: `showLoader`,
            newValue: false,
        });

        // If actionObject doesnt exist, it means the user is either finished with the survey, already did the survey or the survey is closed
        if (response.action === `SurveyEnd`) {
            router.replace({
                name: `Endpage`,
                params: { message: actionObject.message },
            });
        } else if (response.action === `Redirect`) {
            // If we redirect, we do need a loader to show until the redirect is finished
            this.commit(`changeGlobal`, {
                variableName: `showLoader`,
                newValue: true,
            });
            location.replace(actionObject.redirectUrl);
        } else if (response.action === `ShowSurveyPage`) {
            // We don't have to navigate to this view if we are already there
            if (router.currentRoute.name !== `Questionnaire`)
                router.replace(`Questionnaire`);
        }

        if (parameters.arg && parameters.arg.callback) parameters.arg.callback();
    },
    nextButtonClicked: function (vuex, arg = {}) {
        let page = vuex.state.page;
        let nextButtonClicked = arg.nextButtonClicked === undefined ? true : false;
        let allQuestionsareValid = vuex.state.page?.questions.every(q => q.isValid);
        this.commit(`changeGlobal`, {
            variableName: `nextButtonClicked`,
            newValue: nextButtonClicked,
        });

        // If we have validation errors scroll to the first validation with the id of validation-popup.
        // So it shows the user a clearer view of what is happening or needs to be done to continue.
        setTimeout(() => {
            if (!this.state.global.formIsValid || !allQuestionsareValid) {
                document.querySelectorAll(`[data-validation-popup]`)[0]?.scrollIntoView({ behavior: `smooth` });
            }
        }, 100);

        // Table questions have to fold open when there is an invalid field
        if (page) {
            let tableQuestion = page.questions.filter(q => (q.type === `TableSingle` || q.type === `TableMulti` || q.type === `TableMixed`) && q.displayType !== `Matrix`);
            if (tableQuestion.length > 0) {
                let invalidInput = document.querySelector(`input:invalid`);

                if (invalidInput) {
                    let optionIcon = invalidInput.closest(`[data-answer]`).querySelector(`[data-option-label]`);

                    if (optionIcon) {
                        optionIcon.click();
                        setTimeout(() => {
                            invalidInput.focus();
                        }, 0);
                    }
                }
            }
        }

        if (page && page.lastPage) {
            router.replace(`Endpage`);
            return;
        } else {
            vuex.dispatch(`loadPageData`, {
                actionType: arg.actionType || `Default`,
            });
        }
    },
    // Load all the localized text files. When there is a backend this part should be improved upon
    loadText(vuex, arg) {
        const unix = Math.round(+new Date() / 1000);
        // Get the timestamp of the day. We only want caching for a single day
        // Depends on where the culture is coming from
        const culture =
            arg ||
            this.state.surveyInfo.culture ||
            this.state.surveyInfo.queryParams.culture;
        const domainName = document.location.host.includes(`mwm`) ? `mwm2.nl` : `crowdtech.com`;
        const staticDomain = `https://static.${domainName}/Dist/Js/Text/Text.${culture}.js?version=${unix}`;

        // set survey language on html lang attribute
        if (culture !== undefined) document.documentElement.lang = culture;
        else return;

        methods.xhr({
            type: "GET",
            setContentTypeHeader: false,
            setApiKeyHeader: false,
            setAuthorizationHeader: false,
            url: staticDomain,
            callback: (xhr) => {
                let response = JSON.parse(xhr.responseText);

                // filter all the strings that don't start with rs_. Out of all the 10.000s of strings only ~50 are needed. This is to prevent bugs in IOS.
                let keys = Object.keys(response)
                    .filter(key => key.startsWith(`rs_`))
                    .reduce((obj, key) => {
                        obj[key] = response[key];
                        return obj;
                    }, {});

                vuex.commit(`addText`, {
                    textResources: keys
                });
            }
        });
    },
    changeLanguage(vuex, arg) {
        let surveyInfo = vuex.state.surveyInfo;

        if (surveyInfo.respondentId === `undefined`) surveyInfo.respondentId = null;

        let data = {
            language: arg.language,
            surveyId: surveyInfo.surveyId,
            respondentId: surveyInfo.respondentId,
            sessionId: surveyInfo.sessionId,
            fromLogin: arg.fromLogin
        };

        methods.xhr({
            type: "POST",
            setSurveyId: true,
            url: `${surveyInfo.domain}/v1/Questionnaire/ChangeLanguage`,
            data: data,
            callback: (xhr) => {
                let response = JSON.parse(xhr.responseText);
                let layoutSettings = response.layoutSettings;
                const actionObject = response.actionObject;

                response = apivalidation.validate(response, `changeLanguage`);
                layoutSettings = apivalidation.validate(layoutSettings, `layout`);

                this.commit(`addLayoutValues`, layoutSettings);
                this.commit(`addSurveyInfo`, { actionObject });
                this.commit(`addServerMessages`, response.message);
                this.commit(`addPageData`, { page: {} });

                if (actionObject) {
                    this.commit(`addPageData`, { page: actionObject });
                }

                this.commit(`addSurveyInfo`, { culture: response.culture });
                this.dispatch('loadText');

                this.commit(`changeGlobal`, {
                    variableName: `nextButtonClicked`,
                    newValue: false,
                });
                this.commit(`changeGlobal`, {
                    variableName: `showLoader`,
                    newValue: false,
                });

                // If actionObject doesnt exist, it means the user is either finished with the survey, already did the survey or the survey is closed
                if (response.action === `SurveyEnd`) {
                    router.replace({
                        name: `Endpage`,
                        params: { message: actionObject.endText },
                    });
                } else if (response.action === `Redirect`) {
                    // If we redirect, we do need a loader to show until the redirect is finished
                    this.commit(`changeGlobal`, {
                        variableName: `showLoader`,
                        newValue: true,
                    });
                    location.replace(actionObject.redirectUrl);
                } else if (response.action === `ShowSurveyPage`) {
                    // We don't have to navigate to this view if we are already there
                    if (router.currentRoute.name !== `Questionnaire`)
                        router.replace(`Questionnaire`);
                }

                if (router.currentRoute.name === `Beginpage` || router.currentRoute.name === `Loginpage`) {
                    if (surveyInfo.loginType === `CookieCheck`) {
                        sessionStorage.clear();
                    }

                    let queryParams = surveyInfo.queryParams;

                    let parameters = ``;

                    Object.keys(queryParams).forEach(key => {
                        if (key !== `surveyid` && key !== `culture`) {
                            parameters = parameters += `&${key}=${queryParams[key]}`;
                        }
                    });

                    window.location.href = window.location.origin + "?surveyid=" + surveyInfo.surveyId + "&culture=" + response.culture + parameters;
                }

                if (arg && arg.callback) arg.callback();
            },
        });
    },
};
